import { ref, toValue, type MaybeRefOrGetter } from 'vue';

export interface UseFileDropOptions {
  /** Called with the dropped files (never with an empty list). */
  onDrop: (files: File[]) => void;
  /** While true, drags show as „not here“ and drops are ignored. */
  disabled?: MaybeRefOrGetter<boolean>;
}

/** Whether a drag carries files (not text, links or in-app items). */
function carriesFiles(event: DragEvent): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files');
}

/**
 * File drag and drop onto an area, as event handlers to bind on its root
 * (`v-on="handlers"`). Counts `dragenter`/`dragleave` so crossing child
 * elements does not flicker, reacts only to drags that carry files, and sets
 * the drop effect to `none` while disabled.
 */
export function useFileDrop(options: UseFileDropOptions) {
  /** A file drag is over the area. */
  const isDragActive = ref(false);
  let depth = 0;

  const isDisabled = () => Boolean(toValue(options.disabled));

  function reset() {
    depth = 0;
    isDragActive.value = false;
  }

  const handlers = {
    // A nested folder target stops its drop; reset the outer overlay before
    // that happens without treating the event as a background upload.
    dropCapture: reset,
    dragendCapture: reset,
    dragenter(event: DragEvent) {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      depth += 1;
      isDragActive.value = true;
    },
    dragover(event: DragEvent) {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = isDisabled() ? 'none' : 'copy';
    },
    dragleave(event: DragEvent) {
      if (!carriesFiles(event)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) isDragActive.value = false;
    },
    drop(event: DragEvent) {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      reset();
      if (isDisabled()) return;
      const files = Array.from(event.dataTransfer?.files ?? []);
      if (files.length) options.onDrop(files);
    },
  };

  return { isDragActive, handlers, reset };
}
